// cvpFormat.ts — Native CV file format serialization / deserialization
// Extensions: .cvp (project), .cvt (template), .cvpanel (panel), .cvsite (site)
// All types share one envelope: { cvVersion, type, exportedAt, <payload key> }

import type { CVProject, PanelData, SiteData, ProjectSettings, CastLayoutData } from '../types/panelvar';
import { isCadExport, importCadExport } from './cadImportAdapter';
import type { CadExportEnvelope } from './cadImportAdapter';
import { isCadProjectExport, importCadProject } from './cadProjectAdapter';
import type { CadProjectExport } from './cadProjectAdapter';
import { isCadSiteExport, importCadSite } from './cadSiteAdapter';
import type { CadSiteExport } from './cadSiteAdapter';

export const CV_FORMAT_VERSION = '1.0';

// ── Envelope types ─────────────────────────────────────────────────────────────

export interface CVTemplatePayload {
  name:     string;           // Template display name (carried from source project)
  settings: ProjectSettings;
  panels:   PanelData[];      // id / createdAt / updatedAt stripped; new IDs generated on import
}

export interface CVProjectEnvelope {
  cvVersion:  string;
  type:       'project';
  exportedAt: string;
  project:    CVProject;
}

export interface CVTemplateEnvelope {
  cvVersion:  string;
  type:       'template';
  exportedAt: string;
  template:   CVTemplatePayload;
}

export interface CVPanelEnvelope {
  cvVersion:  string;
  type:       'panel';
  exportedAt: string;
  panel:      PanelData;
}

export interface CVSiteEnvelope {
  cvVersion:  string;
  type:       'site';
  exportedAt: string;
  site:       SiteData;
}

export type CVFileEnvelope =
  | CVProjectEnvelope
  | CVTemplateEnvelope
  | CVPanelEnvelope
  | CVSiteEnvelope;

// ── Internal helpers ───────────────────────────────────────────────────────────

function downloadJSON(content: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(content, null, 2)], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function safeName(name: string): string {
  return name.replace(/[^a-zA-Z0-9_\-]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') || 'cv';
}

// ── Export ─────────────────────────────────────────────────────────────────────
//
// Each format has a *pure* `serialize*` that returns the envelope object and
// an `export*` that wraps the serializer in a browser download. Tests call the
// serializer directly so round-trips can run under Node without jsdom hacks.

/** Lift dual-view extras from a SiteData back to the bundle wire shape:
 *  `project.layout` + `project.activeView` as siblings of `project.site`, and
 *  `project.site` itself wrapped as `{present, dwgName, nodKey, nodEntity, payload}`.
 *  Returns a plain object suitable for JSON serialization. CV-800 / CV-751. */
function emitProjectPayload(project: CVProject): Record<string, unknown> {
  const site = project.site;
  if (!site || (!site._cadEnvelope && !site.layout && !site.activeView)) {
    return project as unknown as Record<string, unknown>;
  }
  // Strip dual-view extras from the SiteData copy that lives inside project.*
  // so they don't double-emit (once nested, once at project-level).
  const { layout, activeView, _cadEnvelope, ...restSite } = site;
  void restSite;  // wire-shape uses _cadEnvelope only — restSite is intentionally dropped
  const wrappedSite: Record<string, unknown> = {
    present:    true,
    dwgName:    _cadEnvelope?.dwgName    ?? site.drawingNumber,
    nodKey:     _cadEnvelope?.nodKey     ?? 'site',
    siteCount:  _cadEnvelope?.siteCount  ?? 1,
    nodEntity:  _cadEnvelope?.nodEntity,
    payload:    _cadEnvelope?.payload,
  };
  const projectCopy: Record<string, unknown> = {
    ...(project as unknown as Record<string, unknown>),
    site: wrappedSite,
  };
  if (layout)     projectCopy['layout']     = layout;
  if (activeView) projectCopy['activeView'] = activeView;
  return projectCopy;
}

export function serializeProjectCVP(project: CVProject, exportedAt?: string): Record<string, unknown> {
  return {
    cvVersion:  CV_FORMAT_VERSION,
    type:       'project',
    exportedAt: exportedAt ?? new Date().toISOString(),
    project:    emitProjectPayload(project),
  };
}

export function exportProjectCVP(project: CVProject): void {
  downloadJSON(serializeProjectCVP(project), `${safeName(project.name)}.cvp`);
}

export function exportTemplateCVT(project: CVProject): void {
  // Strip all instance-specific fields so the template is job-neutral
  const templatePanels: PanelData[] = project.panels.map(p => ({
    ...p,
    id:        '',
    createdAt: '',
    updatedAt: '',
    mpvar: {
      ...p.mpvar,
      projectName:   '',
      drawingNumber: '',
      jobNumber:     '',
      date:          '',
      drawBy:        '',
      checkBy:       '',
      notes:         '',
    },
  }));

  const payload: CVTemplatePayload = {
    name:     project.name,
    settings: project.settings,
    panels:   templatePanels,
  };

  const envelope: CVTemplateEnvelope = {
    cvVersion:  CV_FORMAT_VERSION,
    type:       'template',
    exportedAt: new Date().toISOString(),
    template:   payload,
  };
  downloadJSON(envelope, `${safeName(project.name)}.cvt`);
}

export function serializePanelCVPanel(panel: PanelData, exportedAt?: string): CVPanelEnvelope {
  return {
    cvVersion:  CV_FORMAT_VERSION,
    type:       'panel',
    exportedAt: exportedAt ?? new Date().toISOString(),
    panel,
  };
}

export function exportPanelCVPanel(panel: PanelData): void {
  downloadJSON(serializePanelCVPanel(panel), `${safeName(panel.mpvar.panelMark || 'panel')}.cvpanel`);
}

/** Pure serializer for a .cvsite envelope. When the SiteData was loaded from a
 *  dual-view wrapped envelope (cv-cad cvxpsite + cv-attach layout), re-emits
 *  the wrapped form so the round-trip is byte-lossless against the cv-cad
 *  source. CV-800 / CV-751 Stage 3. Returns `null` when there is no site. */
export function serializeSiteCVSite(site: SiteData | null, projectName: string, exportedAt?: string): Record<string, unknown> | null {
  void projectName;  // accepted only to mirror exportSiteCVSite's filename derivation
  if (!site) return null;
  const ts = exportedAt ?? new Date().toISOString();
  if (site._cadEnvelope || site.layout || site.activeView) {
    const wrapped: Record<string, unknown> = {
      cvVersion:  CV_FORMAT_VERSION,
      type:       'site',
      exportedAt: ts,
      site: {
        present:    true,
        dwgName:    site._cadEnvelope?.dwgName    ?? site.drawingNumber,
        nodKey:     site._cadEnvelope?.nodKey     ?? 'site',
        siteCount:  site._cadEnvelope?.siteCount  ?? 1,
        nodEntity:  site._cadEnvelope?.nodEntity,
        payload:    site._cadEnvelope?.payload,
      },
    };
    if (site.layout)     wrapped['layout']     = site.layout;
    if (site.activeView) wrapped['activeView'] = site.activeView;
    return wrapped;
  }
  return {
    cvVersion:  CV_FORMAT_VERSION,
    type:       'site',
    exportedAt: ts,
    site,
  };
}

export function exportSiteCVSite(project: CVProject): void {
  const envelope = serializeSiteCVSite(project.site, project.name);
  if (!envelope) return;
  downloadJSON(envelope, `${safeName(project.name)}_site.cvsite`);
}

// ── Parse & validate ───────────────────────────────────────────────────────────

export type ParseResult =
  | { ok: true;  envelope: CVFileEnvelope; warnings?: string[] }
  | { ok: true;  cadImport: true; panels: PanelData[]; warnings: string[] }
  | { ok: true;  cadProjectImport: true; project: CVProject; warnings: string[] }
  | { ok: true;  cadSiteImport: true; site: SiteData; warnings: string[] }
  | { ok: false; error: string };

export function parseCVFile(json: string): ParseResult {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return { ok: false, error: 'File is not valid JSON.' };
  }

  if (typeof raw !== 'object' || raw === null) {
    return { ok: false, error: 'File is empty or not a CV file.' };
  }

  const obj = raw as Record<string, unknown>;

  if (typeof obj['cvVersion'] !== 'string') {
    return { ok: false, error: 'Not a CV file — missing cvVersion field. For old .cvproj.json files, use File → Open Project.' };
  }

  // ── cv-cad named export detection ──────────────────────────────────────────
  // cv-cad exports have panelCount + panels[] with flat positional keys
  // (mainPanel_1_1, standardOpenings_2_3, etc.) instead of *var objects.
  if (isCadExport(obj)) {
    const result = importCadExport(obj as unknown as CadExportEnvelope);
    return { ok: true, cadImport: true, panels: result.panels, warnings: result.warnings };
  }

  // ── cv-cad project export detection ────────────────────────────────────────
  // cvxpproj.lsp exports: type="project-named-export" with titleBlock + xrecordHeaders
  if (isCadProjectExport(obj)) {
    const result = importCadProject(obj as unknown as CadProjectExport);
    return { ok: true, cadProjectImport: true, project: result.project, warnings: result.warnings };
  }

  // ── cv-cad site export detection ───────────────────────────────────────────
  // cvxpsite.lsp exports: type="site-named-export" with sites[] containing grid/wall data
  if (isCadSiteExport(obj)) {
    const result = importCadSite(obj as unknown as CadSiteExport);
    return { ok: true, cadSiteImport: true, site: result.site, warnings: result.warnings };
  }

  const type = obj['type'];
  if (type !== 'project' && type !== 'template' && type !== 'panel' && type !== 'site') {
    return { ok: false, error: `Unknown CV file type: "${String(type)}".` };
  }

  // Version migration hook — no-op at v1.0; add shims here for future versions
  // if (obj['cvVersion'] !== CV_FORMAT_VERSION) { migrate(obj); }

  switch (type) {
    case 'project': {
      const project = obj['project'];
      if (!project || typeof project !== 'object') {
        return { ok: false, error: 'Malformed .cvp file — missing project payload.' };
      }
      const proj = project as Record<string, unknown>;
      if (!Array.isArray(proj['panels'])) {
        return { ok: false, error: 'Malformed .cvp file — panels is not an array.' };
      }
      // Dual-view bundle (CV-800 / CV-751 Stage 7): cv-cad emits the .cvp with
      //   project.site = { present, dwgName, nodKey, nodEntity, payload }
      //   project.layout / project.activeView as siblings of project.site
      // Normalize to a flat CVProject where the dual-view extras ride on
      // project.site (SiteData.layout / activeView / _cadEnvelope).
      const siteRaw = proj['site'];
      if (siteRaw && typeof siteRaw === 'object') {
        const siteObj = siteRaw as Record<string, unknown>;
        const payload = siteObj['payload'];
        if (siteObj['present'] === true && payload && typeof payload === 'object'
            && isCadSiteExport(payload as Record<string, unknown>)) {
          const result = importCadSite(payload as unknown as CadSiteExport);
          const enriched: SiteData = {
            ...result.site,
            _cadEnvelope: {
              dwgName:   typeof siteObj['dwgName']    === 'string' ? siteObj['dwgName']    : undefined,
              nodKey:    typeof siteObj['nodKey']     === 'string' ? siteObj['nodKey']     : undefined,
              siteCount: typeof siteObj['siteCount']  === 'number' ? siteObj['siteCount']  : undefined,
              nodEntity: siteObj['nodEntity'],
              payload,
            },
          };
          const layout = proj['layout'];
          if (layout && typeof layout === 'object') {
            enriched.layout = layout as unknown as CastLayoutData;
          }
          const activeView = proj['activeView'];
          if (activeView === 'tu' || activeView === 'lo') {
            enriched.activeView = activeView;
          }
          // Mutate a shallow project copy with the normalized site + strip the
          // bundle-level extras now that they live on the SiteData.
          const normalizedProject: Record<string, unknown> = { ...proj, site: enriched };
          delete normalizedProject['layout'];
          delete normalizedProject['activeView'];
          const normalizedEnvelope: CVProjectEnvelope = {
            cvVersion:  obj['cvVersion'] as string,
            type:       'project',
            exportedAt: typeof obj['exportedAt'] === 'string' ? obj['exportedAt'] as string : '',
            project:    normalizedProject as unknown as CVProject,
          };
          return { ok: true, envelope: normalizedEnvelope };
        }
      }
      return { ok: true, envelope: raw as CVProjectEnvelope };
    }
    case 'template': {
      const template = obj['template'];
      if (!template || typeof template !== 'object') {
        return { ok: false, error: 'Malformed .cvt file — missing template payload.' };
      }
      return { ok: true, envelope: raw as CVTemplateEnvelope };
    }
    case 'panel': {
      const panel = obj['panel'];
      if (!panel || typeof panel !== 'object') {
        return { ok: false, error: 'Malformed .cvpanel file — missing panel payload.' };
      }
      return { ok: true, envelope: raw as CVPanelEnvelope };
    }
    case 'site': {
      const site = obj['site'];
      if (!site || typeof site !== 'object') {
        return { ok: false, error: 'Malformed .cvsite file — missing site payload.' };
      }
      const siteObj = site as Record<string, unknown>;
      // Dual-view wrapped envelope (CV-800 / CV-751 Stage 3): cv-cad emits
      //   { site: { present, dwgName, nodKey, nodEntity, payload }, layout, activeView }
      // where site.payload is the cvxpsite named export. Unwrap it through
      // the cad-site adapter and attach layout + activeView + the raw wrapper
      // (for lossless re-export).
      const payload = siteObj['payload'];
      if (siteObj['present'] === true && payload && typeof payload === 'object'
          && isCadSiteExport(payload as Record<string, unknown>)) {
        const result = importCadSite(payload as unknown as CadSiteExport);
        const enriched: SiteData = {
          ...result.site,
          _cadEnvelope: {
            dwgName:   typeof siteObj['dwgName']    === 'string' ? siteObj['dwgName']    : undefined,
            nodKey:    typeof siteObj['nodKey']     === 'string' ? siteObj['nodKey']     : undefined,
            siteCount: typeof siteObj['siteCount']  === 'number' ? siteObj['siteCount']  : undefined,
            nodEntity: siteObj['nodEntity'],
            payload,
          },
        };
        const layout = obj['layout'];
        if (layout && typeof layout === 'object') {
          enriched.layout = layout as unknown as import('../types/panelvar').CastLayoutData;
        }
        const activeView = obj['activeView'];
        if (activeView === 'tu' || activeView === 'lo') {
          enriched.activeView = activeView;
        }
        return { ok: true, cadSiteImport: true, site: enriched, warnings: result.warnings };
      }
      return { ok: true, envelope: raw as CVSiteEnvelope };
    }
  }
}
